import { useCallback } from 'react'
import { useMutation, useReactiveVar } from '@apollo/client'
import { Button, Flex, Heading, Text } from '@radix-ui/themes'
import { MergeGuestSessionMutation } from '__generated__/graphql'
import { MERGE_GUEST_SESSION } from 'api/guest-session/merge-guest-session'
import {
  isConnectWalletDialogOpenVar,
  isSignUpDialogOpenVar
} from 'features/auth/store/dialogs'
import { resetWizardState } from 'features/auth/utils/reset-wizard-state'
import { DataTestIDs } from 'shared/constants'
import { CupColoredIcon, UpDownNavIcon } from 'shared/icons'
import upWinnerDecorationsPath from 'shared/icons/background-element-icons/up-down-winner-decorations.svg'
import { isGuestClaimDialogOpenVar } from 'shared/store/dialogs'
import { XyroDialog, XyroLoading } from 'shared/ui'
import { showNotificationToast } from 'shared/utils/notify'
import { notifyOnUnknownError } from 'shared/utils/notify-on-error'
import styles from './guest-claim-dialog.module.scss'

const closeAuthDialogs = () => {
  isSignUpDialogOpenVar(false)
  isConnectWalletDialogOpenVar(false)
  resetWizardState()
}

const notifyClaimResult = (
  result?: MergeGuestSessionMutation['mergeGuestSession']
) => {
  if (!result || result.alreadyClaimed) return

  const score = result.upDownAi.scoreMigrated
  const rank = result.upDownAi.rankDaily
  const details = [
    score != null ? `Score ${score > 0 ? '+' : ''}${score}` : null,
    rank != null ? `Daily rank #${rank}` : null
  ]
    .filter(Boolean)
    .join(' · ')

  showNotificationToast({
    type: 'success',
    title: 'Demo score claimed',
    description:
      details || 'Your demo results are on the Human vs AI leaderboard.'
  })
}

export const GuestClaimDialog = () => {
  const isOpen = useReactiveVar(isGuestClaimDialogOpenVar)
  const [mergeGuestSession, { loading }] =
    useMutation<MergeGuestSessionMutation>(MERGE_GUEST_SESSION)

  const handleClaim = useCallback(async () => {
    try {
      const { data } = await mergeGuestSession({
        refetchQueries: [
          'getUpDownAiLeaderboard',
          'getMyUpDownAiLeaderboardPosition'
        ]
      })

      isGuestClaimDialogOpenVar(false)
      closeAuthDialogs()
      notifyClaimResult(data?.mergeGuestSession)
    } catch (error) {
      notifyOnUnknownError(error as Error)
    }
  }, [mergeGuestSession])

  return (
    <XyroDialog
      open={isOpen}
      className={styles.dialogContainer}
      isCloseButtonEnabled={false}
    >
      <div className={styles.body}>
        <UpDownNavIcon className={styles.backgroundIcon} />
        <img
          className={styles.decorations}
          src={upWinnerDecorationsPath}
          alt=''
        />

        <Flex
          className={styles.content}
          direction='column'
          align='center'
          justify='center'
          width='100%'
        >
          <div className={styles.iconWrap}>
            <CupColoredIcon
              width='11rem'
              height='11rem'
            />
          </div>

          <Text
            size='2'
            weight='bold'
            className={styles.badge}
          >
            Human vs AI
          </Text>

          <Heading
            as='h3'
            size='7'
            align='center'
            className={styles.title}
          >
            Claim your demo score
          </Heading>

          <Text
            size='4'
            align='center'
            className={styles.description}
          >
            Your demo results are ready. Claim them to join the Human vs AI
            leaderboard.
          </Text>

          <Button
            size='4'
            color='green'
            disabled={loading}
            onClick={handleClaim}
            className={styles.claimButton}
            data-testid={DataTestIDs.buttonGuestClaim}
          >
            <XyroLoading loading={loading}>Claim</XyroLoading>
          </Button>
        </Flex>
      </div>
    </XyroDialog>
  )
}
